import dayjs from 'dayjs'
import { formatVnd } from '@/lib/format-money'
import { currentLocale, i18n } from '@/lib/i18n'

/** Một trường hồ sơ động do một kết nối đồng bộ về (khớp backend contact-attributes). */
export interface ConnAttr {
  key: string
  label: string
  value: string
  valueType: string
  group: string | null
  sourceAt: string | null
}

export interface ConnCluster {
  source: string
  label: string
  syncedAt: string
  attributes: ConnAttr[]
}

/** Định dạng giá trị theo kiểu (chuỗi hiển thị). */
export function formatAttrValue(a: Pick<ConnAttr, 'value' | 'valueType'>): string {
  const v = a.value
  switch (a.valueType) {
    case 'money': return Number.isFinite(Number(v)) ? formatVnd(Number(v)) : v
    case 'number': return Number.isFinite(Number(v)) ? new Intl.NumberFormat(currentLocale()).format(Number(v)) : v
    case 'date': return dayjs(v).isValid() ? dayjs(v).format('DD/MM/YYYY') : v
    case 'datetime': return dayjs(v).isValid() ? dayjs(v).format('HH:mm DD/MM/YYYY') : v
    case 'bool': return v === 'true' ? i18n.t('sharedMisc:attrFormat.yes') : v === 'false' ? i18n.t('sharedMisc:attrFormat.no') : v
    default: return v
  }
}

export type ClusterSort = 'connection' | 'recent' | 'most'

export const CLUSTER_SORT_LABELS: Record<ClusterSort, string> = {
  get connection() { return i18n.t('sharedMisc:attrFormat.sort.connection') },
  get recent() { return i18n.t('sharedMisc:attrFormat.sort.recent') },
  get most() { return i18n.t('sharedMisc:attrFormat.sort.most') },
}

/** Sắp xếp các cụm. `connection` giữ thứ tự máy chủ trả (kết nối nội bộ trước, ứng dụng ngoài sau). */
export function sortClusters(clusters: ConnCluster[], sort: ClusterSort): ConnCluster[] {
  const list = [...clusters]
  if (sort === 'recent') return list.sort((a, b) => b.syncedAt.localeCompare(a.syncedAt))
  if (sort === 'most') return list.sort((a, b) => b.attributes.length - a.attributes.length || a.label.localeCompare(b.label, currentLocale()))
  return list
}

/** Gom trường của một cụm theo nhóm con, giữ thứ tự xuất hiện; trường không nhóm → "Khác" ở cuối. */
export function groupAttrs(attrs: ConnAttr[]): { title: string | null; items: ConnAttr[] }[] {
  const order: string[] = []
  const by = new Map<string, ConnAttr[]>()
  const other: ConnAttr[] = []
  for (const a of attrs) {
    if (!a.group) { other.push(a); continue }
    if (!by.has(a.group)) { by.set(a.group, []); order.push(a.group) }
    by.get(a.group)!.push(a)
  }
  const out = order.map((t) => ({ title: t as string | null, items: by.get(t)! }))
  if (other.length) out.push({ title: out.length ? i18n.t('sharedMisc:attrFormat.other') : null, items: other })
  return out
}
